import { create, type StoreApi, type UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";
import type { Attachment, FirstAttachment } from "@/entities/attachment";
import type { ThreadListItem } from "@/entities/thread";
import { getThreadTitle } from "@/entities/thread";

/** SHA256 hex (64 桁、小文字) のみを許容する正規表現 */
const SHA256_HEX_RE = /^[a-f0-9]{64}$/;

/** 初期 state slice (action は zustand creator が後付けする) */
const initialNgStateSlice = {
  enabled: true,
  showNgContent: false,
  hiddenThreadIds: [] as string[],
  ngDisplayIds: [] as string[],
  ngTitles: [] as string[],
  ngWords: [] as string[],
  ngRegexes: [] as string[],
  ngImages: [] as string[],
};

/** 入力を SHA256 hex 形式に正規化 (大文字→小文字、空白除去) */
function normalizeSha256(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  return SHA256_HEX_RE.test(trimmed) ? trimmed : null;
}

interface NgState {
  enabled: boolean;
  showNgContent: boolean;
  /** 新APIではスレッドIDが ULID(string) になったため string[] で保持する */
  hiddenThreadIds: string[];
  ngDisplayIds: string[];
  ngTitles: string[];
  ngWords: string[];
  ngRegexes: string[];
  /** SHA256 hex (64 桁、小文字)。旧 API の知覚ハッシュ (64bit binary) からは仕様変更 */
  ngImages: string[];

  setEnabled: (enabled: boolean) => void;
  setShowNgContent: (show: boolean) => void;
  hideThread: (id: string) => void;
  unhideThread: (id: string) => void;
  addNgDisplayId: (displayId: string) => void;
  removeNgDisplayId: (displayId: string) => void;
  addNgTitle: (title: string) => void;
  removeNgTitle: (title: string) => void;
  addNgWord: (word: string) => void;
  removeNgWord: (word: string) => void;
  addNgRegex: (regex: string) => void;
  removeNgRegex: (regex: string) => void;
  addNgImage: (input: string) => void;
  removeNgImage: (hash: string) => void;

  isThreadHidden: (thread: ThreadListItem) => boolean;
  /**
   * NG 判定対象。
   * - displayId: ID NG リスト判定用
   * - body: 本文 NG ワード/正規表現判定用 (parsed しているかは呼び出し側依存)
   * - attachmentHashes: 添付の SHA256 hex 列。与えられた全 hash を ngImages と突合
   */
  isPostHidden: (post: {
    displayId: string | null;
    body: string;
    attachmentHashes?: readonly string[] | null;
  }) => boolean;
  clearAllNgSettings: () => void;
}

// ─── 外部から呼び出し可能なヘルパー関数 ───
/**
 * Attachment / FirstAttachment から画像 NG リストへ追加 (コンポーネント外からも利用可能)。
 * 新 API では sha256Hash (64 桁 hex) を直接突合する。
 */
export function addNgImageFromAttachment(
  attachment: Attachment | FirstAttachment | null | undefined,
): void {
  if (!attachment?.sha256Hash) return;
  useNgStore.getState().addNgImage(attachment.sha256Hash);
}

// NG処理本体 useNgStore
export const useNgStore: UseBoundStore<StoreApi<NgState>> = create<NgState>()(
  persist(
    (set, get) => ({
      enabled: true,
      showNgContent: false,
      hiddenThreadIds: [],
      ngDisplayIds: [],
      ngTitles: [],
      ngWords: [],
      ngRegexes: [],
      ngImages: [],

      setEnabled: (enabled: boolean) => set({ enabled }),
      setShowNgContent: (showNgContent: boolean) => set({ showNgContent }),

      hideThread: (id: string) =>
        set((s: NgState) => ({
          hiddenThreadIds: s.hiddenThreadIds.includes(id)
            ? s.hiddenThreadIds
            : [...s.hiddenThreadIds, id],
        })),

      unhideThread: (id: string) =>
        set((s: NgState) => ({
          hiddenThreadIds: s.hiddenThreadIds.filter((i) => i !== id),
        })),

      addNgDisplayId: (displayId: string) =>
        set((s: NgState) => ({
          ngDisplayIds: s.ngDisplayIds.includes(displayId)
            ? s.ngDisplayIds
            : [...s.ngDisplayIds, displayId],
        })),

      removeNgDisplayId: (displayId: string) =>
        set((s: NgState) => ({
          ngDisplayIds: s.ngDisplayIds.filter((d) => d !== displayId),
        })),

      addNgTitle: (title: string) =>
        set((s: NgState) => ({
          ngTitles: s.ngTitles.includes(title)
            ? s.ngTitles
            : [...s.ngTitles, title],
        })),

      removeNgTitle: (title: string) =>
        set((s: NgState) => ({
          ngTitles: s.ngTitles.filter((t) => t !== title),
        })),

      addNgWord: (word: string) =>
        set((s: NgState) => ({
          ngWords: s.ngWords.includes(word) ? s.ngWords : [...s.ngWords, word],
        })),

      removeNgWord: (word: string) =>
        set((s: NgState) => ({
          ngWords: s.ngWords.filter((w) => w !== word),
        })),

      addNgRegex: (regex: string) => {
        try {
          new RegExp(regex);
        } catch {
          return;
        }
        set((s: NgState) => ({
          ngRegexes: s.ngRegexes.includes(regex)
            ? s.ngRegexes
            : [...s.ngRegexes, regex],
        }));
      },

      removeNgRegex: (regex: string) =>
        set((s: NgState) => ({
          ngRegexes: s.ngRegexes.filter((r) => r !== regex),
        })),

      addNgImage: (input: string) => {
        const hash = normalizeSha256(input);
        if (!hash) return;
        set((s: NgState) => ({
          ngImages: s.ngImages.includes(hash)
            ? s.ngImages
            : [...s.ngImages, hash],
        }));
      },

      removeNgImage: (hash: string) =>
        set((s: NgState) => ({
          ngImages: s.ngImages.filter((h) => h !== hash),
        })),

      isThreadHidden: (thread: ThreadListItem) => {
        const state = get();
        if (!state.enabled) return false;
        if (state.hiddenThreadIds.includes(thread.id)) return true;

        // ThreadListItem に body フィールドが無いため、本文 NG ワード/正規表現は
        // スレ詳細ロード時 (ThreadView) で判定する (Issue #285 案 A)。ここは
        // title マッチと OP 添付の SHA256 突合のみ行う。
        const title = getThreadTitle(thread);

        for (const ng of state.ngTitles) {
          if (title.includes(ng)) return true;
        }

        for (const ng of state.ngWords) {
          if (title.includes(ng)) return true;
        }

        for (const pattern of state.ngRegexes) {
          try {
            if (new RegExp(pattern).test(title)) return true;
          } catch {
            // Invalid regex, skip
          }
        }

        const opHash = thread.firstAttachment?.sha256Hash;
        if (opHash && state.ngImages.includes(opHash)) return true;

        return false;
      },

      isPostHidden: (post: {
        displayId: string | null;
        body: string;
        attachmentHashes?: readonly string[] | null;
      }) => {
        const state = get();
        if (!state.enabled) return false;

        const joinedBody = post.body ?? "";

        // displayId でチェック
        if (post.displayId && state.ngDisplayIds.includes(post.displayId)) {
          return true;
        }

        for (const ng of state.ngWords) {
          if (joinedBody.includes(ng)) return true;
        }

        for (const pattern of state.ngRegexes) {
          try {
            if (new RegExp(pattern).test(joinedBody)) return true;
          } catch {
            // Invalid regex, skip
          }
        }

        // 画像 NG (SHA256 完全一致)
        if (post.attachmentHashes && state.ngImages.length > 0) {
          for (const hash of post.attachmentHashes) {
            if (hash && state.ngImages.includes(hash)) return true;
          }
        }

        return false;
      },

      clearAllNgSettings: () =>
        set({
          enabled: true,
          showNgContent: false,
          hiddenThreadIds: [],
          ngDisplayIds: [],
          ngTitles: [],
          ngWords: [],
          ngRegexes: [],
          ngImages: [],
        }),
    }),
    {
      name: "aimg-ng-settings",
      version: 2,
      // version 2: ngImages を perceptual hash の 64bit 二進列 → SHA256 hex に変更。
      // version 1 以前で保存された 64 桁の 0/1 文字列はもう照合できないので破棄する。
      migrate: (persisted: unknown, version: number): NgState => {
        // persisted が壊れている / null / 非 object のときは初期 state slice を返して
        // 後段で .hiddenThreadIds など参照しても TypeError にならないようにする。
        // action は zustand creator が後付けで bind するためここでは省略してよい。
        if (!persisted || typeof persisted !== "object") {
          return { ...initialNgStateSlice } as unknown as NgState;
        }
        const state = persisted as NgState;
        if (version < 2) {
          // 旧 API では hiddenThreadIds が number[] だった可能性があるので String() 化
          const normalizedHiddenThreadIds = Array.isArray(state.hiddenThreadIds)
            ? state.hiddenThreadIds.map(String)
            : [];
          // ngImages は perceptual hash の 64bit 二進列 → SHA256 hex に変更。
          // toLowerCase で正規化してから SHA256 hex のみ残す。
          const normalizedNgImages = Array.isArray(state.ngImages)
            ? state.ngImages
                .map((h) => (typeof h === "string" ? h.toLowerCase() : ""))
                .filter((h) => SHA256_HEX_RE.test(h))
            : [];
          return {
            ...state,
            hiddenThreadIds: normalizedHiddenThreadIds,
            ngImages: normalizedNgImages,
          };
        }
        return state;
      },
    },
  ),
);
